import React, { useState, useMemo } from 'react';
import {
  BookOpen,
  Plus,
  Search,
  ShieldCheck,
  FileCheck,
  Layers,
  Printer,
  UserCheck,
  ArrowLeft
} from 'lucide-react';
import { EditorialProject } from '../../../../types/EditorialProject.types';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { RowActionsMenu, viewAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';

import EditorialPhase1Evaluation from './editorial/EditorialPhase1Evaluation';
import EditorialPhase2Contracts from './editorial/EditorialPhase2Contracts';
import EditorialPhase3Production from './editorial/EditorialPhase3Production';
import EditorialPhase4Closing from './editorial/EditorialPhase4Closing';
import NewProposalModal from './editorial/NewProposalModal';
import '../shared-modules.css';

const cn = (...args: any[]) => args.filter(Boolean).join(' ');

interface EditorialModuleProps {
  projects: EditorialProject[];
  setProjects: React.Dispatch<React.SetStateAction<EditorialProject[]>>;
}

const EDITORIAL_PHASE_LABELS: Record<string, { label: string; badge: string }> = {
  evaluacion: { label: '1. Evaluación', badge: 'ed-badge--amber' },
  contratacion: { label: '2. Contratación', badge: 'ed-badge--sky' },
  produccion: { label: '3. Producción', badge: 'ed-badge--purple' },
  cierre: { label: '4. Cierre / Royalties', badge: 'ed-badge--green' }
};

export default function EditorialModule({ projects, setProjects }: EditorialModuleProps) {
  const [currentView, setCurrentView] = useState<'list' | 'detail'>('list');
  const [activePhaseTab, setActivePhaseTab] = useState<'fase1' | 'fase2' | 'fase3' | 'fase4'>('fase1');

  const [searchQuery, setSearchQuery] = useState('');
  const [phaseFilter, setPhaseFilter] = useState<string>('all');
  const [modalityFilter, setModalityFilter] = useState<string>('all');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const [isNewProposalModalOpen, setIsNewProposalModalOpen] = useState(false);

  const filteredProjects = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return projects.filter(p => {
      const matchesSearch =
        p.bookTitle.toLowerCase().includes(query) ||
        p.bookAuthor.toLowerCase().includes(query) ||
        p.id.toLowerCase().includes(query) ||
        (p.clientName && p.clientName.toLowerCase().includes(query));
      const matchesPhase = phaseFilter === 'all' || p.editorialPhase === phaseFilter;
      const matchesModality = modalityFilter === 'all' || p.modality === modalityFilter;
      return matchesSearch && matchesPhase && matchesModality;
    });
  }, [projects, searchQuery, phaseFilter, modalityFilter]);

  const activeProject = useMemo(() => {
    return projects.find(p => p.id === selectedProjectId) || null;
  }, [projects, selectedProjectId]);

  const handleUpdateProject = (updatedProject: EditorialProject) => {
    setProjects(prev => prev.map(p => (p.id === updatedProject.id ? updatedProject : p)));
  };

  const handleCreateProject = (newProject: EditorialProject) => {
    setProjects(prev => [newProject, ...prev]);
    setSelectedProjectId(newProject.id);
    setActivePhaseTab('fase1');
    setCurrentView('detail');
  };

  const handleViewProject = (p: EditorialProject) => {
    setSelectedProjectId(p.id);
    if (p.editorialPhase === 'evaluacion') setActivePhaseTab('fase1');
    else if (p.editorialPhase === 'contratacion') setActivePhaseTab('fase2');
    else if (p.editorialPhase === 'produccion') setActivePhaseTab('fase3');
    else if (p.editorialPhase === 'cierre') setActivePhaseTab('fase4');
    else setActivePhaseTab('fase1');
    setCurrentView('detail');
  };

  const handleAdvanceToPhase2 = (p: EditorialProject) => { handleUpdateProject(p); setActivePhaseTab('fase2'); };
  const handleAdvanceToPhase3 = (p: EditorialProject) => { handleUpdateProject(p); setActivePhaseTab('fase3'); };
  const handleAdvanceToPhase4 = (p: EditorialProject) => { handleUpdateProject(p); setActivePhaseTab('fase4'); };

  const totalBudget = projects.reduce((acc, p) => acc + (p.globalBudget || 0), 0);
  const totalAdvances = projects.reduce((acc, p) => acc + (p.contractDetails?.advanceAmount || 0), 0);
  const totalCopiesPrinted = projects.reduce((acc, p) => acc + (p.printJob?.effectiveDelivered || p.tiradas[0]?.quantity || 0), 0);

  const activeProjectRules = useMemo(() => {
    if (!activeProject) return { passedCount: 0 };
    const r1Pass = !!activeProject.zeroReader?.report?.completed;
    const r2Pass = activeProject.contractDetails?.status === 'signed';
    const r3Pass = !!activeProject.imprimatur?.approved;
    const remainingAdv = Math.max(0, (activeProject.contractDetails?.advanceAmount || 0) - (activeProject.authorLiquidation?.grossRoyalties || 0));
    const r4Pass = remainingAdv === 0 || !!activeProject.authorLiquidation?.isLiquidated;
    return { passedCount: [r1Pass, r2Pass, r3Pass, r4Pass].filter(Boolean).length };
  }, [activeProject]);

  const columns: ColumnDef<EditorialProject>[] = [
    {
      id: 'id',
      header: 'ID_REF',
      render: (p) => <span style={{ color: 'var(--color-brand-green)', fontWeight: 700 }}>{p.id}</span>
    },
    {
      id: 'bookTitle',
      header: 'TÍTULO DE LA OBRA',
      render: (p) => <span style={{ color: 'var(--color-brand-text-bright)', fontWeight: 700 }} title={p.bookTitle}>{p.bookTitle}</span>
    },
    {
      id: 'bookAuthor',
      header: 'AUTOR',
      render: (p) => <span style={{ color: '#cbd5e1' }} title={p.bookAuthor}>{p.bookAuthor}</span>
    },
    {
      id: 'modality',
      header: 'MODALIDAD',
      render: (p) => (
        <span className={cn('ed-badge', p.modality === 'encargo' ? 'ed-badge--purple' : 'ed-badge--sky')}>
          {p.modality === 'encargo' ? `Encargo: ${p.clientName || 'Cliente'}` : 'Inversión Propia'}
        </span>
      )
    },
    {
      id: 'editorialPhase',
      header: 'FASE ACTUAL',
      render: (p) => {
        const phaseInfo = EDITORIAL_PHASE_LABELS[p.editorialPhase || ''] || { label: p.editorialPhase || '—', badge: '' };
        return <span className={cn('ed-badge', phaseInfo.badge)}>{phaseInfo.label}</span>;
      }
    },
    {
      id: 'price',
      header: 'PVP',
      align: 'right',
      render: (p) => <span style={{ fontWeight: 700, color: 'var(--color-brand-text-bright)' }}>€{(p.formats[0]?.price || 22.00).toFixed(2)}</span>
    },
    {
      id: 'globalBudget',
      header: 'PRESUPUESTO',
      align: 'right',
      render: (p) => <span style={{ fontWeight: 700, color: 'var(--color-brand-green)' }}>€{(p.globalBudget || 0).toLocaleString()}</span>
    },
    {
      id: 'targetReleaseDate',
      header: 'LANZAMIENTO',
      render: (p) => <span style={{ color: '#94a3b8' }}>{p.targetReleaseDate}</span>
    }
  ];

  return (
    <div className="ed-root">
      <div className="module-header-row">
        <ModuleHeader
          title="Módulo_Editorial"
          subtitle={
            currentView === 'detail' && activeProject
              ? `Expediente: ${activeProject.bookTitle} (${activeProject.id}) — ${activeProject.bookAuthor}`
              : 'Gestión y seguimiento del catálogo de obras y ciclo integral: evaluación, contratación, producción industrial y liquidación de royalties.'
          }
        />
        {currentView === 'detail' ? (
          <button onClick={() => setCurrentView('list')} className="ed-back-btn">
            <ArrowLeft size={14} style={{ color: 'var(--color-brand-green)' }} /> Regresar al Catálogo
          </button>
        ) : (
          <button onClick={() => setIsNewProposalModalOpen(true)} className="module-create-btn">
            <Plus size={14} /> Nueva Propuesta / Proyecto
          </button>
        )}
      </div>

      {currentView === 'list' ? (
        <div className="ed-kpi-grid">
          <div className="ed-kpi-card">
            <span className="ed-kpi-label">Proyectos en Cartera</span>
            <span className="ed-kpi-value">{projects.length} Obras</span>
            <span className="ed-kpi-caption">Activas en editorial</span>
          </div>
          <div className="ed-kpi-card">
            <span className="ed-kpi-label">Presupuesto Comprometido</span>
            <span className="ed-kpi-value ed-kpi-value--green">{totalBudget.toLocaleString()} €</span>
            <span className="ed-kpi-caption">Coste editorial global</span>
          </div>
          <div className="ed-kpi-card">
            <span className="ed-kpi-label">Anticipos de Derechos</span>
            <span className="ed-kpi-value ed-kpi-value--amber">{totalAdvances.toLocaleString()} €</span>
            <span className="ed-kpi-caption">Advance a compensar</span>
          </div>
          <div className="ed-kpi-card">
            <span className="ed-kpi-label">Tiradas Industriales</span>
            <span className="ed-kpi-value ed-kpi-value--sky">{totalCopiesPrinted.toLocaleString()} Uds</span>
            <span className="ed-kpi-caption">Ejemplares impresos/plan</span>
          </div>
          <div className="ed-kpi-card ed-kpi-card--wide">
            <span className="ed-kpi-label">Control de Bloqueos</span>
            <span className="ed-kpi-value ed-kpi-value--green ed-kpi-icon-row"><ShieldCheck size={18} /> 4 Reglas OK</span>
            <span className="ed-kpi-caption">Validación canónica activa</span>
          </div>
        </div>
      ) : activeProject ? (
        <div className="ed-kpi-grid">
          <div className="ed-kpi-card">
            <span className="ed-kpi-label">Fase / Modalidad</span>
            <span className="ed-kpi-value" style={{ fontSize: '1rem' }}>{EDITORIAL_PHASE_LABELS[activeProject.editorialPhase || 'evaluacion']?.label || '1. Evaluación'}</span>
            <span className="ed-kpi-caption">{activeProject.modality === 'encargo' ? `Por Encargo (${activeProject.clientName || 'Cliente'})` : 'Inversión Propia (Fondo)'}</span>
          </div>
          <div className="ed-kpi-card">
            <span className="ed-kpi-label">Presupuesto Proyecto</span>
            <span className="ed-kpi-value ed-kpi-value--green">{(activeProject.globalBudget || 0).toLocaleString()} €</span>
            <span className="ed-kpi-caption">PVP Ref: {(activeProject.formats?.[0]?.price || 22).toFixed(2)} € ({activeProject.formats?.[0]?.name || 'Papel'})</span>
          </div>
          <div className="ed-kpi-card">
            <span className="ed-kpi-label">Anticipo de Derechos</span>
            <span className="ed-kpi-value ed-kpi-value--amber">{(activeProject.contractDetails?.advanceAmount || 0).toLocaleString()} €</span>
            <span className="ed-kpi-caption">
              {activeProject.contractDetails?.status === 'signed' ? 'Firmado (DocuSign)' : activeProject.contractDetails?.status === 'docusign_sent' ? 'DocuSign enviado' : 'Pendiente firma'} • Amort: {(activeProject.contractDetails?.advanceAmortized || 0).toLocaleString()} €
            </span>
          </div>
          <div className="ed-kpi-card">
            <span className="ed-kpi-label">Tirada Industrial</span>
            <span className="ed-kpi-value ed-kpi-value--sky">{(activeProject.printJob?.contractedRun || activeProject.tiradas?.[0]?.quantity || 0).toLocaleString()} Uds</span>
            <span className="ed-kpi-caption">{activeProject.printJob?.printShop || 'Imprenta por asignar'} • {(activeProject.printJob?.status || 'planificada').replace('_', ' ').toUpperCase()}</span>
          </div>
          <div className="ed-kpi-card ed-kpi-card--wide">
            <span className="ed-kpi-label">Control de Bloqueos</span>
            <span className={cn('ed-kpi-value ed-kpi-icon-row', activeProjectRules.passedCount === 4 ? 'ed-kpi-value--green' : 'ed-kpi-value--amber')}>
              <ShieldCheck size={18} /> {activeProjectRules.passedCount}/4 Reglas OK
            </span>
            <span className="ed-kpi-caption">{activeProjectRules.passedCount === 4 ? 'Validación canónica superada' : `${4 - activeProjectRules.passedCount} regla(s) pendiente(s)`}</span>
          </div>
        </div>
      ) : null}

      {currentView === 'list' ? (
        <div className="ed-col-stack">
        <div className="module-toolbar">
          <div className="module-search-wrapper">
            <Search className="module-search-icon" size={14} />
            <input
              type="text"
              placeholder="BUSCAR PROYECTO POR TÍTULO, AUTOR, ID O CLIENTE..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="module-search-input"
            />
          </div>

          <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)} className="module-filter-select">
            <option value="all">TODAS_LAS_FASES</option>
            <option value="evaluacion">1. EVALUACIÓN Y MANUSCRITO</option>
            <option value="contratacion">2. VIABILIDAD Y DERECHOS</option>
            <option value="produccion">3. PRODUCCIÓN EDITORIAL</option>
            <option value="cierre">4. CIERRE Y ROYALTIES</option>
          </select>

          <select value={modalityFilter} onChange={(e) => setModalityFilter(e.target.value)} className="module-filter-select">
            <option value="all">TODAS_LAS_MODALIDADES</option>
            <option value="propia">INVERSIÓN PROPIA</option>
            <option value="encargo">POR ENCARGO</option>
          </select>
        </div>

          {/* El expediente no cabe en un panel lateral (4 fases con sus propios
              formularios): se abre a pantalla completa, sustituyendo al catálogo. */}
          <DataTable
            data={filteredProjects}
            columns={columns}
            title={<><BookOpen size={14} className="module-panel-title-icon" /> Catálogo_Proyectos_Editoriales</>}
            subtitle={`REGISTROS: ${filteredProjects.length}`}
            getRowId={(p) => p.id}
            onRowClick={handleViewProject}
            emptyText="NO SE ENCONTRARON PROYECTOS REGISTRADOS CON ESTOS FILTROS."
            actionsHeader="ACCIÓN"
            actionsColumn={(p) => <RowActionsMenu actions={[viewAction(() => handleViewProject(p))]} />}
          />
        </div>
      ) : (
        <div className="ed-col-stack" style={{ gap: '1.5rem' }}>
          <div className="ed-tabs">
            <button onClick={() => setActivePhaseTab('fase1')} className={cn('ed-tab', activePhaseTab === 'fase1' && 'ed-tab--active')}>
              <UserCheck size={14} /> 1. Evaluación y Manuscrito
              {activeProject?.zeroReader?.report?.completed && <span className="ed-tab-dot" />}
            </button>
            <button onClick={() => setActivePhaseTab('fase2')} className={cn('ed-tab', activePhaseTab === 'fase2' && 'ed-tab--active')}>
              <FileCheck size={14} /> 2. Viabilidad y Derechos (DocuSign)
              {activeProject?.contractDetails?.status === 'signed' && <span className="ed-tab-dot" />}
            </button>
            <button onClick={() => setActivePhaseTab('fase3')} className={cn('ed-tab', activePhaseTab === 'fase3' && 'ed-tab--active')}>
              <Layers size={14} /> 3. Producción Editorial &amp; Gantt
              {activeProject?.imprimatur?.approved && <span className="ed-tab-dot" />}
            </button>
            <button onClick={() => setActivePhaseTab('fase4')} className={cn('ed-tab', activePhaseTab === 'fase4' && 'ed-tab--active')}>
              <Printer size={14} /> 4. Cierre, Impresión y Royalties
              {activeProject?.authorLiquidation?.isLiquidated && <span className="ed-tab-dot" />}
            </button>
          </div>

          {activeProject ? (
            <div>
              {activePhaseTab === 'fase1' && <EditorialPhase1Evaluation project={activeProject} onUpdateProject={handleUpdateProject} onAdvanceToPhase2={handleAdvanceToPhase2} />}
              {activePhaseTab === 'fase2' && <EditorialPhase2Contracts project={activeProject} onUpdateProject={handleUpdateProject} onAdvanceToPhase3={handleAdvanceToPhase3} />}
              {activePhaseTab === 'fase3' && <EditorialPhase3Production project={activeProject} onUpdateProject={handleUpdateProject} onAdvanceToPhase4={handleAdvanceToPhase4} />}
              {activePhaseTab === 'fase4' && <EditorialPhase4Closing project={activeProject} onUpdateProject={handleUpdateProject} />}
            </div>
          ) : (
            <div className="ed-detail-empty">No hay proyectos registrados con los filtros seleccionados.</div>
          )}
        </div>
      )}

      <NewProposalModal isOpen={isNewProposalModalOpen} onClose={() => setIsNewProposalModalOpen(false)} onCreateProject={handleCreateProject} />
    </div>
  );
}
